<template>
<Teleport to="body">
    <Transition name="settings-modal">
        <div
            v-if="modelValue"
            class="settings-overlay"
            @click.self="close"
        >
            <section
                ref="dialog"
                class="settings-dialog"
                role="dialog"
                aria-modal="true"
                aria-label="Настройки"
                tabindex="-1"
            >
                <aside class="settings-sidebar">
                    <nav
                        class="settings-categories"
                        role="tablist"
                        aria-label="Разделы настроек"
                        aria-orientation="vertical"
                    >
                        <button
                            v-for="(section, index) in sections"
                            :id="`settings-tab-${section.id}`"
                            :key="section.id"
                            ref="categoryButtons"
                            type="button"
                            class="settings-category"
                            :class="{ 'settings-category-active': activeSection === section.id }"
                            role="tab"
                            :aria-selected="activeSection === section.id ? 'true' : 'false'"
                            :aria-controls="`settings-panel-${section.id}`"
                            :tabindex="activeSection === section.id ? 0 : -1"
                            @click="activeSection = section.id"
                            @keydown="handleCategoryKeydown($event, index)"
                        >
                            <span class="settings-category-icon" aria-hidden="true">
                                <svg
                                    v-if="section.id === 'appearance'"
                                    class="settings-command-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    focusable="false"
                                >
                                    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/>
                                    <circle cx="12" cy="12" r="2.5"/>
                                </svg>
                                <svg
                                    v-else-if="section.id === 'hotkeys'"
                                    class="settings-command-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    focusable="false"
                                >
                                    <path d="M18 9a3 3 0 1 0-3-3v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12Z"/>
                                </svg>
                                <span v-else class="settings-developer-glyph">{ }</span>
                            </span>
                            {{ section.label }}
                        </button>
                    </nav>
                </aside>

                <div class="settings-content">
                    <section
                        v-if="activeSection === 'appearance'"
                        id="settings-panel-appearance"
                        class="settings-section"
                        role="tabpanel"
                        aria-labelledby="settings-tab-appearance"
                        tabindex="0"
                    >
                        <div class="settings-list">
                            <div class="settings-item">
                                <div>
                                    <h3 id="visible-scrollbar-title">Видимый скроллбар</h3>
                                    <p id="visible-scrollbar-description">
                                        Показывать вертикальные и горизонтальные полосы прокрутки на сайте.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    class="settings-switch"
                                    role="switch"
                                    :aria-checked="visibleScrollbar ? 'true' : 'false'"
                                    aria-labelledby="visible-scrollbar-title"
                                    aria-describedby="visible-scrollbar-description"
                                    @click="toggleVisibleScrollbar"
                                >
                                    <span class="settings-switch-thumb" aria-hidden="true"></span>
                                </button>
                            </div>
                            <div class="settings-item">
                                <div>
                                    <h3 id="hero-trailers-title">Трейлеры в hero</h3>
                                    <p id="hero-trailers-description">
                                        Воспроизводить фоновые трейлеры в главном баннере.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    class="settings-switch"
                                    role="switch"
                                    :aria-checked="heroTrailersEnabled ? 'true' : 'false'"
                                    aria-labelledby="hero-trailers-title"
                                    aria-describedby="hero-trailers-description"
                                    @click="toggleHeroTrailers"
                                >
                                    <span class="settings-switch-thumb" aria-hidden="true"></span>
                                </button>
                            </div>
                            <div class="settings-item">
                                <div>
                                    <h3>Классический дизайн</h3>
                                    <p>Открыть оригинальную версию интерфейса Shinai.</p>
                                </div>
                                <router-link
                                    to="/classic"
                                    class="settings-navigation"
                                    @click="close"
                                >Открыть</router-link>
                            </div>
                        </div>
                    </section>

                    <section
                        v-if="activeSection === 'developer'"
                        id="settings-panel-developer"
                        class="settings-section"
                        role="tabpanel"
                        aria-labelledby="settings-tab-developer"
                        tabindex="0"
                    >
                        <div class="settings-list">
                            <div class="settings-item">
                                <div>
                                    <h3>Данные приложения</h3>
                                    <p>Удаляет библиотеку, историю просмотра и локальные данные Shinai.</p>
                                </div>
                                <button
                                    type="button"
                                    class="settings-delete"
                                    :disabled="clearing"
                                    @click="deleteAllData"
                                >
                                    {{ clearing ? 'Удаляем…' : 'Удалить все данные' }}
                                </button>
                            </div>
                        </div>
                        <p v-if="errorMessage" class="settings-error" role="alert">
                            {{ errorMessage }}
                        </p>
                    </section>

                    <section
                        v-if="activeSection === 'hotkeys'"
                        id="settings-panel-hotkeys"
                        class="settings-section"
                        role="tabpanel"
                        aria-labelledby="settings-tab-hotkeys"
                        tabindex="0"
                    >
                        <div class="settings-list">
                            <div class="settings-item settings-hotkey-item">
                                <p class="settings-hotkey-description">
                                    <kbd>Пробел</kbd><span> — для пролистывания контента hero</span>
                                </p>
                            </div>
                        </div>
                    </section>
                </div>
            </section>
        </div>
    </Transition>
</Teleport>
</template>

<script>
import { clearApplicationData } from '@/services/appData.js'
import { readAppearance, writeAppearance } from '@/services/appearance.js'

const FOCUSABLE_SELECTOR = [
    'button:not([disabled]):not([tabindex="-1"])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',')

export default {
    name: 'SettingsModal',
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const appearance = readAppearance()
        return {
            sections: [
                { id: 'appearance', label: 'Внешний вид' },
                { id: 'developer', label: 'Для разработчиков' },
                { id: 'hotkeys', label: 'Горячие клавиши' },
            ],
            activeSection: 'appearance',
            visibleScrollbar: appearance.visibleScrollbar,
            heroTrailersEnabled: appearance.heroTrailersEnabled,
            previouslyFocusedElement: null,
            active: false,
            clearing: false,
            errorMessage: '',
        }
    },
    watch: {
        modelValue(value, previousValue) {
            if (value && !previousValue) this.open()
            if (!value && previousValue) this.finishClosing()
        },
    },
    mounted() {
        if (this.modelValue) this.open()
    },
    beforeUnmount() {
        this.finishClosing(false)
    },
    methods: {
        open() {
            if (this.active) return
            this.active = true
            this.errorMessage = ''
            this.previouslyFocusedElement = document.activeElement
            document.body.classList.add('settings-modal-open')
            window.addEventListener('keydown', this.handleKeydown, true)
            this.$nextTick(() => {
                this.$refs.dialog?.focus({ preventScroll: true })
            })
        },
        close() {
            if (this.clearing) return
            this.$emit('update:modelValue', false)
        },
        finishClosing(restoreFocus = true) {
            window.removeEventListener('keydown', this.handleKeydown, true)
            document.body.classList.remove('settings-modal-open')
            if (
                this.active
                && restoreFocus
                && this.previouslyFocusedElement
                && typeof this.previouslyFocusedElement.focus === 'function'
            ) {
                this.previouslyFocusedElement.focus({ preventScroll: true })
            }
            this.active = false
            this.clearing = false
            this.previouslyFocusedElement = null
        },
        focusableElements() {
            const dialog = this.$refs.dialog
            if (!dialog) return []
            return [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)]
                .filter((element) => !element.hasAttribute('disabled') && element.tabIndex >= 0)
        },
        handleCategoryKeydown(event, currentIndex) {
            const lastIndex = this.sections.length - 1
            const nextIndexByKey = {
                ArrowDown: currentIndex === lastIndex ? 0 : currentIndex + 1,
                ArrowUp: currentIndex === 0 ? lastIndex : currentIndex - 1,
                Home: 0,
                End: lastIndex,
            }
            const nextIndex = nextIndexByKey[event.key]
            if (!Number.isInteger(nextIndex)) return

            event.preventDefault()
            this.activeSection = this.sections[nextIndex].id
            this.$nextTick(() => {
                this.$refs.categoryButtons?.[nextIndex]?.focus({ preventScroll: true })
            })
        },
        handleKeydown(event) {
            if (event.key === 'Escape' || Number(event.keyCode) === 461) {
                event.preventDefault()
                event.stopPropagation()
                this.close()
                return
            }
            if (event.key !== 'Tab') return

            const dialog = this.$refs.dialog
            const elements = this.focusableElements()
            if (!dialog || !elements.length) {
                event.preventDefault()
                dialog?.focus({ preventScroll: true })
                return
            }

            const first = elements[0]
            const last = elements[elements.length - 1]
            const focusOutside = document.activeElement === dialog
                || !dialog.contains(document.activeElement)
            if (event.shiftKey && (document.activeElement === first || focusOutside)) {
                event.preventDefault()
                last.focus({ preventScroll: true })
            } else if (!event.shiftKey && (document.activeElement === last || focusOutside)) {
                event.preventDefault()
                first.focus({ preventScroll: true })
            }
        },
        toggleVisibleScrollbar() {
            const appearance = writeAppearance({
                visibleScrollbar: !this.visibleScrollbar,
            })
            this.visibleScrollbar = appearance.visibleScrollbar
        },
        toggleHeroTrailers() {
            const appearance = writeAppearance({
                heroTrailersEnabled: !this.heroTrailersEnabled,
            })
            this.heroTrailersEnabled = appearance.heroTrailersEnabled
        },
        async deleteAllData() {
            if (this.clearing) return
            this.clearing = true
            this.errorMessage = ''

            const result = clearApplicationData()
            if (result.errors.length) {
                this.errorMessage = 'Не удалось удалить локальные данные. Попробуйте ещё раз.'
                this.clearing = false
                return
            }

            await this.$store.dispatch('reset_library')
            const destination = this.$router.resolve({ name: 'HomeView' }).href
            window.location.replace(destination)
        },
    },
}
</script>

<style scoped>
.settings-overlay {
    position: fixed;
    z-index: 11000;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    inset: 0;
    display: grid;
    place-items: center;
    box-sizing: border-box;
    padding: 24px;
    background: rgba(0, 0, 0, .76);
    font-family: Inter, Arial, sans-serif;
}

.settings-dialog {
    position: relative;
    display: grid;
    grid-template-columns: 250px minmax(0, 1fr);
    width: calc(100vw - 48px);
    max-width: 900px;
    width: min(900px, calc(100vw - 48px));
    max-height: calc(100vh - 48px);
    max-height: calc(100dvh - 48px);
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, .1);
    border-radius: 18px;
    background: #111;
    color: #fff;
    box-shadow: 0 28px 90px rgba(0, 0, 0, .58);
}

.settings-dialog:focus {
    outline: none;
}

.settings-category:focus-visible,
.settings-delete:focus-visible,
.settings-navigation:focus-visible,
.settings-switch:focus-visible,
.settings-section:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 3px;
}

.settings-sidebar,
.settings-content {
    box-sizing: border-box;
    padding: 28px 16px;
}

.settings-sidebar {
    min-height: 420px;
    border-right: 1px solid rgba(255, 255, 255, .08);
    background: #0d0d0d;
}

.settings-categories {
    display: grid;
    gap: 6px;
}

.settings-category {
    display: flex;
    align-items: center;
    gap: 0;
    width: 100%;
    min-height: 48px;
    padding: 0 13px;
    border: 0;
    border-radius: 11px;
    background: transparent;
    color: rgba(255, 255, 255, .72);
    font: 600 14px/1.25 Inter, Arial, sans-serif;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
}

.settings-category-active {
    background: linear-gradient(135deg, #d40000 0%, #ff3e3e 100%);
    color: #fff;
}

.settings-category-icon {
    flex: 0 0 24px;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    margin-right: 11px;
    color: currentColor;
}

.settings-command-icon {
    display: block;
    width: 18px;
    height: 18px;
    overflow: visible;
    pointer-events: none;
}

.settings-developer-glyph {
    font: 700 14px/1 Roboto Mono, monospace;
}

.settings-content {
    min-width: 0;
    overflow-y: auto;
}

.settings-section {
    outline: none;
}

.settings-list {
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, .09);
    border-radius: 14px;
    background: #171717;
}

.settings-item {
    --settings-row-inset: 20px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    min-height: 82px;
    padding: 18px var(--settings-row-inset);
    box-sizing: border-box;
}

.settings-item > div {
    min-width: 0;
}

.settings-item + .settings-item::before {
    position: absolute;
    top: 0;
    right: var(--settings-row-inset);
    left: var(--settings-row-inset);
    height: 1px;
    background: rgba(255, 255, 255, .09);
    content: '';
}

.settings-item h3 {
    margin: 0;
    font-size: 17px;
    line-height: 1.3;
}

.settings-item p {
    max-width: 430px;
    margin: 7px 0 0;
    color: rgba(255, 255, 255, .52);
    font-size: 13px;
    line-height: 1.45;
}

.settings-delete {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    min-height: 42px;
    margin-left: auto;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: #ff5a5a;
    font: 650 13px/1 Inter, Arial, sans-serif;
    text-align: right;
    cursor: pointer;
    transition: color 160ms ease, opacity 160ms ease;
}

.settings-delete:hover:not(:disabled) {
    border: 0;
    background: transparent;
    color: #ff7676;
}

.settings-delete:disabled {
    cursor: wait;
    opacity: .62;
}

.settings-navigation {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    margin-left: auto;
    padding: 0 18px;
    border-radius: 999px;
    background: rgba(255, 255, 255, .12);
    color: #fff;
    font: 650 13px/1 Inter, Arial, sans-serif;
    text-decoration: none;
    transition: background 160ms ease, color 160ms ease;
}

.settings-navigation:hover {
    background: rgba(255, 255, 255, .2);
}

.settings-switch {
    position: relative;
    flex: 0 0 auto;
    width: 48px;
    height: 28px;
    margin-left: auto;
    padding: 4px;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, .18);
    cursor: pointer;
    transition: background 160ms ease;
}

.settings-switch-thumb {
    display: block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, .35);
    transform: translateX(0);
    transition: transform 160ms ease;
}

.settings-switch[aria-checked="true"] {
    background: #e31b23;
}

.settings-switch[aria-checked="true"] .settings-switch-thumb {
    transform: translateX(20px);
}

.settings-error {
    margin: 14px 0 0;
    color: #ff6a6a;
    font-size: 13px;
    line-height: 1.4;
}

.settings-hotkey-item {
    min-height: 64px;
}

.settings-hotkey-description {
    max-width: none !important;
    margin: 0 !important;
    color: rgba(255, 255, 255, .72) !important;
}

.settings-hotkey-description kbd {
    color: #fff;
    font: 650 13px/1 Inter, Arial, sans-serif;
}

.settings-modal-enter-active,
.settings-modal-leave-active {
    transition: opacity 200ms ease;
}

.settings-modal-enter-active .settings-dialog,
.settings-modal-leave-active .settings-dialog {
    transition: opacity 200ms ease, transform 200ms ease;
}

.settings-modal-enter-from,
.settings-modal-leave-to,
.settings-modal-enter-from .settings-dialog,
.settings-modal-leave-to .settings-dialog {
    opacity: 0;
}

.settings-modal-enter-from .settings-dialog,
.settings-modal-leave-to .settings-dialog {
    transform: translateY(-12px);
}

@media (max-width: 700px) {
    .settings-overlay {
        align-items: center;
        padding: 14px;
    }

    .settings-dialog {
        grid-template-columns: minmax(0, 1fr);
        width: calc(100vw - 28px);
        max-height: calc(100dvh - 28px);
    }

    .settings-sidebar {
        min-height: 0;
        padding: 18px 14px;
        border-right: 0;
        border-bottom: 1px solid rgba(255, 255, 255, .08);
    }

    .settings-category {
        min-height: 44px;
    }

    .settings-content {
        padding: 18px 14px;
    }

    .settings-item {
        align-items: stretch;
        flex-direction: column;
        gap: 0;
        padding: 18px;
    }

    .settings-delete {
        width: auto;
        margin-top: 18px;
        margin-left: 0;
        align-self: flex-end;
    }

    .settings-switch {
        align-self: flex-end;
        margin-top: 18px;
        margin-left: 0;
    }

    .settings-navigation {
        align-self: flex-end;
        margin-top: 18px;
        margin-left: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .settings-modal-enter-active,
    .settings-modal-leave-active,
    .settings-modal-enter-active .settings-dialog,
    .settings-modal-leave-active .settings-dialog,
    .settings-delete,
    .settings-navigation,
    .settings-switch,
    .settings-switch-thumb {
        transition: none;
    }
}
</style>

<style>
body.settings-modal-open {
    overflow: hidden;
}
</style>
